// POP tag and job record agreement (Session AVS, BOB1 "Tap reconciliation"). A section of the Lift performance page, over
// the same date window. It shows where the tag record and the job record DISAGREE, as office information: completed visits
// on a tagged lift with no tap that day (service visits and call-outs only, the two the Home scan matches; a repair or technician visit is logged on the job sheet and never listed), and taps with no visit at that lift. It is derived live on the server and stored
// nowhere; it names no engineer; and it never reads as a fault, because the tap never gates a visit and a missing tap can
// simply mean the tag was not used. Visits on lifts with no tag (or a tag not yet proven) and imported or backdated records
// are counted apart, never listed.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { TapReconciliationPanel, TapReconciliationFinding } from "../../lib/api";
import { formatDate, formatTime } from "../../lib/format";
import { EmptyState } from "../../components/EmptyState";

type SortBy = "day" | "lift" | "site" | "kind";

const KIND_LABEL: Record<TapReconciliationFinding["kind"], string> = {
  visit_without_tap: "Visit, no tap that day",
  tap_without_visit: "Tap, no visit recorded",
};

export function TapReconciliationSection({ from, to }: { from: string; to: string }) {
  const [kind, setKind] = useState<"" | TapReconciliationFinding["kind"]>("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>("day");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<TapReconciliationPanel | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A new window or filter starts again at page one.
  useEffect(() => setPage(1), [from, to, kind, search, sortBy, dir]);

  useEffect(() => {
    let active = true;
    setError(null);
    api.reporting
      .tapReconciliation({ from: from || undefined, to: to || undefined, kind: kind || undefined, search: search.trim() || undefined, sortBy, dir, page, pageSize: 25 })
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError("The POP tag comparison could not be loaded. Please try again.");
      });
    return () => {
      active = false;
    };
  }, [from, to, kind, search, sortBy, dir, page]);

  const c = data?.counts;
  const notApplicable = c ? c.visitsNotApplicable : 0;

  return (
    <section aria-labelledby="tap-recon-heading" className="mt-6 rounded-ele border border-ele-purple-accent bg-ele-surface-soft p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 id="tap-recon-heading" className="font-heading text-xl text-ele-blue">
          POP tag and job record
        </h2>
        {data ? <span className="font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">{formatDate(data.window.from)} to {formatDate(data.window.to)}</span> : null}
      </div>
      <p className="mb-4 text-sm text-ele-body">
        Where the tag record and the job record do not agree, for the office to look at. A missing tap does not mean anything went wrong: the tag is never
        required to complete a visit.
      </p>

      {error ? (
        <p role="alert" className="text-sm text-ele-danger">
          {error}
        </p>
      ) : data === null ? (
        <p className="text-sm text-ele-body opacity-70">Reading the tag record</p>
      ) : (
        <>
          <dl className="mb-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <div>
              <dt className="text-ele-body opacity-70">Visits checked</dt>
              <dd className="font-heading text-lg text-ele-strong">{c!.visitsConsidered}</dd>
            </div>
            <div>
              <dt className="text-ele-body opacity-70">Service visits and call-outs with no tap that day</dt>
              <dd className="font-heading text-lg text-ele-strong">{c!.visitsWithoutTap}</dd>
            </div>
            <div>
              <dt className="text-ele-body opacity-70">Taps with no visit</dt>
              <dd className="font-heading text-lg text-ele-strong">{c!.tapsWithoutVisit}</dd>
            </div>
            <div>
              <dt className="text-ele-body opacity-70">Not applicable</dt>
              <dd className="font-heading text-lg text-ele-strong">{notApplicable}</dd>
            </div>
          </dl>
          <p className="mb-4 text-xs text-ele-body opacity-70">
            Not applicable: {c!.notApplicableByReason.no_tag} on a lift with no tag, {c!.notApplicableByReason.tag_not_proven} where the tag has not been proven by a first tap,{" "}
            {c!.notApplicableByReason.before_tag_proven} before the tag was proven. Left out: {c!.visitsExcludedHistorical} imported or backdated record
            {c!.visitsExcludedHistorical === 1 ? "" : "s"}.
          </p>

          <div className="mb-3 flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-blue">Show</span>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as typeof kind)}
                className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
              >
                <option value="">Both lists</option>
                <option value="visit_without_tap">Service visits and call-outs with no tap that day</option>
                <option value="tap_without_visit">Taps with no visit recorded</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-blue">Search</span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Lift, site or reference"
                className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-blue">Sort by</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
              >
                <option value="day">Date</option>
                <option value="lift">Lift</option>
                <option value="site">Site</option>
                <option value="kind">Type</option>
              </select>
            </label>
            <button
              type="button"
              onClick={() => setDir((d) => (d === "asc" ? "desc" : "asc"))}
              className="rounded-ele border border-ele-purple-accent px-3 py-1.5 text-sm text-ele-body hover:text-ele-strong"
            >
              {dir === "asc" ? "Oldest first" : "Newest first"}
            </button>
          </div>

          {data.findings.length === 0 ? (
            <EmptyState compact message="The tag record and the job record agree for this period and filter." />
          ) : (
            <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
              <table className="ele-list-table w-full text-left text-sm">
                <colgroup>
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "22%" }} />
                  <col style={{ width: "32%" }} />
                </colgroup>
                <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                  <tr>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Lift</th>
                    <th className="px-3 py-2">Site</th>
                    <th className="px-3 py-2">Reference</th>
                    <th className="px-3 py-2">What disagrees</th>
                  </tr>
                </thead>
                <tbody>
                  {data.findings.map((f) => (
                    <tr key={`${f.kind}-${f.jobId ?? f.tapId}`}>
                      <td className="truncate px-3 py-2 text-ele-body">
                        {formatDate(f.day)} {f.kind === "tap_without_visit" ? formatTime(f.at) : ""}
                      </td>
                      <td className="truncate px-3 py-2 text-ele-strong" title={f.ele}>
                        {f.ele}
                      </td>
                      <td className="truncate px-3 py-2 text-ele-body" title={f.siteName}>
                        {f.siteName}
                      </td>
                      <td className="truncate px-3 py-2 text-ele-body" title={f.reference ?? ""}>
                        {f.reference ?? ""}
                      </td>
                      <td className="px-3 py-2 text-ele-body">{KIND_LABEL[f.kind]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.pages > 1 ? (
            <div className="mt-3 flex items-center justify-between text-sm text-ele-body">
              <span>
                Page {data.page} of {data.pages} ({data.total} in all)
              </span>
              <span className="flex gap-2">
                <button type="button" disabled={data.page <= 1} onClick={() => setPage(data.page - 1)} className="rounded-ele border border-ele-purple-accent px-3 py-1 disabled:opacity-40">
                  Previous
                </button>
                <button type="button" disabled={data.page >= data.pages} onClick={() => setPage(data.page + 1)} className="rounded-ele border border-ele-purple-accent px-3 py-1 disabled:opacity-40">
                  Next
                </button>
              </span>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
